import { loginSchema } from '@argus-labs/auth-shared/auth'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  createFileRoute,
  Link,
  redirect,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PageCard } from '@/components/ui/page-card'
import { Text, text } from '@/components/ui/text'
import { authClient } from '@/lib/auth-client'
import { authFlow, flowDestination, flowHeaders, upgradeFailureReason } from '@/lib/auth-flow'
import { otpVerification } from '@/lib/otp-verification'
import { invalidateSessionQuery } from '@/lib/session'
import { toast } from '@/lib/toast'
import { commonSearchSchema, generateTitle } from '@/lib/utils'

// Use with stripSearchParams middleware, so the browser URL remains clean
const defaultSearchParams = {
  currentStep: 'email',
} as const

const loginPageSchema = commonSearchSchema.extend({
  // Maintain current step in the URL, so that user can use the browser's back button to navigate back
  currentStep: z.enum(['email', 'password']).default(defaultSearchParams.currentStep),
  // As multistep progresses, keep previous steps' values in the URL
  email: z.email().optional(),
})

export const Route = createFileRoute('/_signedOut/login')({
  validateSearch: loginPageSchema,
  component: Login,
  head: () => ({ meta: [{ title: generateTitle('Sign In') }] }),
  search: {
    middlewares: [stripSearchParams(defaultSearchParams)],
  },
  beforeLoad: ({ search }) => {
    // In password step, email is required
    if (search.currentStep === 'password' && !search.email) {
      throw redirect({ to: '/login', search: { ...search, currentStep: 'email' } })
    }
  },
})

function getDescription(currentStep: 'email' | 'password') {
  if (currentStep === 'email') {
    return 'Enter your email address to explore our world.'
  }

  if (currentStep === 'password') {
    return 'Enter your password to start playing and making.'
  }
}

function Login() {
  const search = Route.useSearch()
  const { currentStep, gameId } = search
  const isUpgrade = authFlow(search).kind === 'guestUpgrade'

  return (
    <PageCard
      title={isUpgrade ? 'Link Your Guest Progress' : 'Sign In'}
      description={
        isUpgrade ? (
          <>
            Sign in to link your guest progress in <b>{gameId}</b> to your Argus account.
          </>
        ) : (
          getDescription(currentStep)
        )
      }
    >
      <LoginForm />

      <div className="mt-4 flex items-center gap-4">
        <Text size={12}>Don’t have an account?</Text>

        <Link to="/register" className="flex">
          <Text size={12} font="nbArchitekt" color="gold">
            Sign Up
          </Text>
        </Link>
      </div>
    </PageCard>
  )
}

// Login is a multistep form
function LoginForm() {
  const { currentStep, email } = Route.useSearch()

  switch (currentStep) {
    case 'email':
      return <EmailForm />
    case 'password': {
      if (!email) {
        // Shouldn't happen because of beforeLoad in _signedOut/login.tsx
        throw new Error('Email is required when on password step')
      }
      return <PasswordForm email={email} />
    }
    default:
      return null
  }
}

// Pick email from login params
const emailFormSchema = loginSchema.pick({ email: true })
type EmailFormParams = z.infer<typeof emailFormSchema>

function EmailForm() {
  const search = Route.useSearch()
  const form = useForm<EmailFormParams>({
    resolver: zodResolver(emailFormSchema),
    defaultValues: { email: '' },
    mode: 'onChange',
  })
  const navigate = useNavigate()

  const onSubmit = async (values: EmailFormParams) => {
    // Go to password form
    navigate({
      to: '/login',
      search: { ...search, currentStep: 'password', email: values.email },
    })
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2">
      <Input
        control={form.control}
        type="email"
        name="email"
        label="Email Address"
        required
        autoFocus
        autoComplete="email"
        placeholder="mojo.doe@argus.gg"
      />

      <Button type="submit" flex>
        Next
      </Button>
    </form>
  )
}

const passwordFormSchema = loginSchema.omit({
  // not in the form, from search params instead
  email: true,
})
type PasswordFormParams = z.infer<typeof passwordFormSchema>

function PasswordForm({ email }: { email: string }) {
  const flow = authFlow(Route.useSearch())
  const form = useForm<PasswordFormParams>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: { password: '' },
    mode: 'onChange',
  })
  const navigate = useNavigate()

  const onSubmit = async (values: PasswordFormParams) => {
    try {
      const { data, error } = await authClient.signIn.email({
        email,
        password: values.password,
        fetchOptions: {
          headers: flowHeaders(flow),
        },
      })

      if (data) {
        invalidateSessionQuery()

        return navigate(flowDestination(flow))
      }

      if (error.code === 'EMAIL_NOT_VERIFIED') {
        otpVerification.startResendCooldown()
        return navigate({
          to: '/verify',
          search: { email, type: VerifyType.ConfirmEmail },
        })
      }

      const reason = upgradeFailureReason(flow, error.code)
      if (reason) return navigate({ to: '/upgrade-failed', search: { reason } })

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 flex flex-col gap-2">
      <Input
        control={form.control}
        name="password"
        label="Password"
        type="password"
        required
        autoFocus
        autoComplete="current-password"
        placeholder=""
        extraComponent={<ForgotPasswordButton email={email} />}
      />

      <Button type="submit" flex isDisabled={form.formState.isSubmitting}>
        Sign In
      </Button>
    </form>
  )
}

function ForgotPasswordButton({ email }: { email: string }) {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  async function handleClick() {
    try {
      setIsLoading(true)
      const { data, error } = await otpVerification.requestPasswordReset(email)

      if (data) {
        return navigate({ to: '/verify', search: { email, type: VerifyType.PasswordReset } })
      }

      toast.fromError(error)
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button
      type="button"
      className={text({
        font: 'nbArchitekt',
        size: 12,
        color: 'gold',
        className: 'cursor-pointer disabled:opacity-50',
      })}
      disabled={isLoading}
      onClick={handleClick}
    >
      Forgot password?
    </button>
  )
}
